import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2, Plus, Copy, XCircle, RefreshCw, QrCode, Download, X, Check, Share2, Mail, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { useTenant } from '@/contexts/TenantContext';

const STATUS_COLORS = {
  active: 'bg-green-900/30 text-green-400',
  revoked: 'bg-red-900/30 text-red-400',
  expired: 'bg-gray-800 text-gray-500',
};
const STATUS_LABELS = { active: 'Attiva', revoked: 'Revocata', expired: 'Scaduta' };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Costruisce il link universale per la chiave digitale dell'ospite
function buildGuestLink(tokenId, tenantSlug) {
  const base = window.location.origin;
  if (tenantSlug) {
    return `${base}/g/${tokenId}?tenant=${tenantSlug}`;
  }
  return `${base}/key/${tokenId}`;
}

// Sostituisce variabili nel modello con i dati reali della prenotazione
function fillTemplate(tpl, booking, link) {
  if (!tpl) return '';
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch { return iso || ''; }
  };
  return tpl
    .replace(/{nome}/g,      booking?.guest_name      || '')
    .replace(/{struttura}/g, booking?.property_name   || '')
    .replace(/{checkin}/g,   fmt(booking?.checkin_at))
    .replace(/{checkout}/g,  fmt(booking?.checkout_at))
    .replace(/{link}/g,      link || '');
}

export default function Bookings() {
  const { slug: tenantSlug } = useTenant();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState({ msg_whatsapp: '', msg_email_subject: '', msg_email_body: '' });
  const [panelOpen, setPanelOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [properties, setProperties] = useState([]);
  const [devices, setDevices] = useState([]);
  const [form, setForm] = useState({
    property_id: '', guest_name: '', guest_email: '', guest_phone: '',
    num_guests: 2, checkin_at: '', checkout_at: '', total_price: 0, device_ids: []
  });

  // QR modal state
  const [qrModal, setQrModal] = useState(null); // { booking, link }
  const [qrLoading, setQrLoading] = useState(null);
  const qrCanvasRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, pRes, dRes, sRes] = await Promise.all([
        api.get('/bookings'),
        api.get('/properties'),
        api.get('/devices'),
        api.get('/settings'),
      ]);
      setBookings(bRes.data);
      setProperties(pRes.data);
      setDevices(dRes.data);
      setTemplates({
        msg_whatsapp:      sRes.data.msg_whatsapp      || '',
        msg_email_subject: sRes.data.msg_email_subject || '',
        msg_email_body:    sRes.data.msg_email_body    || '',
      });
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyLink = async (booking) => {
    try {
      const link = buildGuestLink(booking.token_id, tenantSlug);
      await navigator.clipboard.writeText(link);
      toast.success('Link copiato negli appunti');
    } catch { toast.error('Errore copia link'); }
  };

  const showQr = async (booking) => {
    setQrLoading(booking.id);
    try {
      const link = buildGuestLink(booking.token_id, tenantSlug);
      setQrModal({ booking, link });
    } catch { toast.error('Errore recupero link'); }
    finally { setQrLoading(null); }
  };

  const downloadQr = () => {
    const canvas = document.getElementById('qr-canvas-download');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `qr-${qrModal?.booking?.guest_name?.replace(/\s+/g, '-') || 'chiave'}.png`;
    a.click();
    toast.success('QR scaricato');
  };

  const copyQrLink = async () => {
    if (!qrModal?.link) return;
    await navigator.clipboard.writeText(qrModal.link);
    toast.success('Link copiato');
  };

  const shareWhatsApp = () => {
    if (!qrModal?.link) return;
    const text = fillTemplate(templates.msg_whatsapp, qrModal.booking, qrModal.link)
      || `Ciao ${qrModal.booking.guest_name}! Ecco la tua chiave digitale:\n${qrModal.link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareEmail = () => {
    if (!qrModal?.link) return;
    const subject = fillTemplate(templates.msg_email_subject, qrModal.booking, qrModal.link)
      || 'La tua chiave digitale';
    const body = fillTemplate(templates.msg_email_body, qrModal.booking, qrModal.link)
      || `Ecco il link alla tua chiave:\n${qrModal.link}`;
    window.open(
      `mailto:${qrModal.booking.guest_email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      '_blank'
    );
  };

  const shareNative = async () => {
    if (!qrModal?.link) return;
    if (!navigator.share) { copyQrLink(); return; }
    try {
      await navigator.share({
        title: 'Chiave digitale Shangrilux',
        text: `Ecco la tua chiave digitale per ${qrModal.booking.guest_name}`,
        url: qrModal.link,
      });
    } catch { /* utente ha annullato */ }
  };

  const revokeKey = async (booking) => {
    const reason = prompt('Motivo revoca (opzionale):') || '';
    try {
      const res = await api.post(`/bookings/${booking.id}/revoke`, { reason });
      setBookings(prev => prev.map(b => b.id === booking.id ? res.data : b));
      toast.success('Chiave revocata');
    } catch { toast.error('Errore revoca'); }
  };

  const createBooking = async () => {
    try {
      const payload = {
        ...form,
        device_ids: form.device_ids || [],
        checkin_at: new Date(form.checkin_at).toISOString(),
        checkout_at: new Date(form.checkout_at).toISOString(),
      };
      await api.post('/bookings', payload);
      toast.success('Prenotazione creata');
      setPanelOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione');
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('it-IT'); } catch { return iso; }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-gold">Gestione</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Chiavi Virtuali</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-ghost p-2" data-testid="refresh-bookings-btn"><RefreshCw size={16} /></button>
          <button onClick={() => { setCreating(true); setPanelOpen(true); }}
            className="btn-gold flex items-center gap-2" data-testid="new-booking-btn">
            <Plus size={14} /> Nuova Chiave
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-[#c9a84c]" /></div>
      ) : (
        <div className="card-dark rounded-lg overflow-hidden">
          {bookings.length === 0 ? (
            <div className="p-16 text-center"><p className="text-[var(--lux-text-muted-3)] text-sm">Nessuna chiave virtuale.</p></div>
          ) : (
            <table className="w-full table-dark">
              <thead>
                <tr>
                  <th className="text-left">Ospite</th>
                  <th className="text-left hidden md:table-cell">Proprietà</th>
                  <th className="text-left hidden sm:table-cell">Check-in</th>
                  <th className="text-left hidden sm:table-cell">Check-out</th>
                  <th className="text-left">Stato</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <p className="text-white text-sm">{b.guest_name}</p>
                      <p className="text-[var(--lux-text-muted-3)] text-xs">{b.guest_email}</p>
                    </td>
                    <td className="hidden md:table-cell text-[var(--lux-text-sec)] text-xs">{b.property_name || '—'}</td>
                    <td className="hidden sm:table-cell text-[var(--lux-text-sec)] text-xs">{fmtDate(b.checkin_at)}</td>
                    <td className="hidden sm:table-cell text-[var(--lux-text-sec)] text-xs">{fmtDate(b.checkout_at)}</td>
                    <td>
                      <span className={`status-badge ${STATUS_COLORS[b.status] || ''}`}>
                        {STATUS_LABELS[b.status] || b.status}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => copyLink(b)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-[#c9a84c] transition-colors"
                          title="Copia link" data-testid={`copy-link-${b.id}`}>
                          <Copy size={13} />
                        </button>
                        <button
                          onClick={() => showQr(b)}
                          disabled={qrLoading === b.id}
                          title="QR Code"
                          data-testid={`qr-btn-${b.id}`}
                          className="p-1.5 transition-colors"
                          style={{ color: qrLoading === b.id ? '#555' : 'rgba(201,168,76,0.6)' }}
                          onMouseEnter={e => e.currentTarget.style.color = '#c9a84c'}
                          onMouseLeave={e => e.currentTarget.style.color = qrLoading === b.id ? '#555' : 'rgba(201,168,76,0.6)'}>
                          {qrLoading === b.id ? <Loader2 size={13} className="animate-spin" /> : <QrCode size={13} />}
                        </button>
                        {b.status === 'active' && (
                          <button onClick={() => revokeKey(b)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-red-400 transition-colors"
                            title="Revoca" data-testid={`revoke-${b.id}`}>
                            <XCircle size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Create panel */}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="bg-[var(--lux-surface-1)] border-l border-[#222] text-white w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-serif text-2xl font-light text-white">Nuova Chiave Virtuale</SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <div>
              <label className="label-muted block mb-1">Proprietà</label>
              <select className="input-dark" value={form.property_id} onChange={e => setForm(p => ({ ...p, property_id: e.target.value }))} data-testid="new-booking-property">
                <option value="">— Seleziona —</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-muted block mb-1">Nome Ospite</label>
              <input className="input-dark" value={form.guest_name} onChange={e => setForm(p => ({ ...p, guest_name: e.target.value }))} data-testid="new-booking-name" />
            </div>
            <div>
              <label className="label-muted block mb-1">Email</label>
              <input className="input-dark" type="email" value={form.guest_email} onChange={e => setForm(p => ({ ...p, guest_email: e.target.value }))} data-testid="new-booking-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-muted block mb-1">Check-in</label>
                <input className="input-dark" type="datetime-local" value={form.checkin_at} onChange={e => setForm(p => ({ ...p, checkin_at: e.target.value }))} data-testid="new-booking-checkin" />
              </div>
              <div>
                <label className="label-muted block mb-1">Check-out</label>
                <input className="input-dark" type="datetime-local" value={form.checkout_at} onChange={e => setForm(p => ({ ...p, checkout_at: e.target.value }))} data-testid="new-booking-checkout" />
              </div>
            </div>
            <div>
              <label className="label-muted block mb-2">Dispositivi</label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {devices.map(d => (
                  <label key={d.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.device_ids.includes(d.id)}
                      onChange={e => setForm(p => ({
                        ...p,
                        device_ids: e.target.checked
                          ? [...p.device_ids, d.id]
                          : p.device_ids.filter(id => id !== d.id)
                      }))}
                      className="accent-[#c9a84c]"
                    />
                    <span className="text-sm text-[var(--lux-text-sec)]">{d.name} <span className="text-[var(--lux-text-muted-3)] text-xs">({d.type})</span></span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-4 border-t border-[#222]">
              <button onClick={createBooking} className="btn-gold flex-1" data-testid="create-booking-submit">Crea Chiave</button>
              <button onClick={() => setPanelOpen(false)} className="btn-outline-gold flex-1">Annulla</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* QR Code Modal */}
      {qrModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={() => setQrModal(null)}>
          <div
            className="rounded-sm p-8 text-center max-w-sm w-full relative"
            style={{ background: 'var(--lux-surface-1)', border: '1px solid rgba(201,168,76,0.35)' }}
            onClick={e => e.stopPropagation()}>

            <button
              onClick={() => setQrModal(null)}
              className="absolute top-4 right-4 p-1.5 rounded-sm"
              style={{ color: 'var(--lux-text-muted)' }}>
              <X size={16} />
            </button>

            <p className="text-[10px] tracking-[3px] uppercase mb-1" style={{ color: '#c9a84c' }}>
              Chiave Virtuale
            </p>
            <h3 className="font-serif text-lg font-light mb-1" style={{ color: 'var(--lux-text)' }}>
              {qrModal.booking.guest_name}
            </h3>
            <p className="text-xs mb-6" style={{ color: 'var(--lux-text-muted)' }}>
              Scansiona per accedere al portale ospite
            </p>

            {/* QR Code — sfondo bianco per leggibilità scanner */}
            <div className="inline-block p-4 rounded-sm mx-auto mb-5"
              style={{ background: '#fff' }}>
              <QRCodeCanvas
                id="qr-canvas-download"
                value={qrModal.link}
                size={200}
                bgColor="#ffffff"
                fgColor="#0a0a0a"
                level="H"
                data-testid="qr-canvas"
              />
            </div>

            {/* Link URL */}
            <div className="flex items-center gap-2 mb-5 text-left">
              <input
                readOnly
                value={qrModal.link}
                data-testid="qr-link-input"
                className="flex-1 px-3 py-2 text-xs rounded-sm font-mono"
                style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)', outline: 'none' }}
              />
              <button
                onClick={copyQrLink}
                data-testid="qr-copy-link-btn"
                className="p-2 rounded-sm flex-shrink-0"
                style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}>
                <Copy size={13} />
              </button>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mb-4">
              <button
                onClick={downloadQr}
                data-testid="qr-download-btn"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-sm"
                style={{ background: '#c9a84c', color: '#000' }}>
                <Download size={13} /> Scarica PNG
              </button>
              <button
                onClick={() => setQrModal(null)}
                className="flex-1 py-2.5 text-sm rounded-sm"
                style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
                Chiudi
              </button>
            </div>

            {/* Condivisione rapida */}
            <div className="border-t pt-4" style={{ borderColor: 'var(--lux-border-1)' }}>
              <p className="text-[10px] uppercase tracking-widest mb-3 text-left" style={{ color: 'var(--lux-text-muted-3)' }}>
                Condividi con l'ospite
              </p>
              <div className="flex gap-2">
                <button
                  onClick={shareWhatsApp}
                  data-testid="share-whatsapp-btn"
                  title="Condividi su WhatsApp"
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm rounded-sm transition-all hover:opacity-90"
                  style={{ background: '#25D366', color: '#fff' }}>
                  <MessageCircle size={14} /> WhatsApp
                </button>
                <button
                  onClick={shareEmail}
                  data-testid="share-email-btn"
                  title="Invia via Email"
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm rounded-sm transition-all"
                  style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}>
                  <Mail size={14} /> Email
                </button>
                {'share' in navigator && (
                  <button
                    onClick={shareNative}
                    data-testid="share-native-btn"
                    title="Condividi…"
                    className="py-2.5 px-3 rounded-sm transition-all"
                    style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
                    <Share2 size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
